import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthManager } from 'src/app/shared/auth/AuthManager';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { LeaderService } from 'src/app/leader-toolkit/services/leader.service';
import { CreateNewTaskComponent } from '../../tasks/create-new-task/create-new-task.component';
import { AssginNewGoalPageComponent } from '../../goals/assgin-new-goal-page/assgin-new-goal-page.component';
import { GoalDetailsComponent } from '../../goals/goal-details/goal-details.component';
import { MeetingDetailDialogComponent } from '../../scheduling/meeting-detail-dialog.component';
import { NewMeetingSchedulerComponent } from '../../scheduling/new-meeting-scheduler/new-meeting-scheduler.component';
import { ViewTaskModalComponent } from '../../tasks/view-task-modal/view-task-modal.component';
import { ConfirmationDialogComponent } from 'src/app/leader-toolkit/components/confirmation-dialog/confirmation-dialog.component';

export interface HospitalMeeting {
  meetingId: number;
  meetingSubject: string;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  joinUrl: string;
  createdUserName: string;
  hospitalName: string;
  /** When present, used to scope meetings to this hospital (preferred over name). */
  hospitalId?: number;
  attendeesNames: string[];
  duration: string;
  timeZone: string;
}

@Component({
  selector: 'vc-hospital-ops-dashboard',
  templateUrl: './hospital-ops-dashboard.component.html',
  styleUrl: './hospital-ops-dashboard.component.scss',
  standalone: false
})
export class HospitalOpsDashboardComponent implements OnInit, OnDestroy {
  isLoading = false;
  isLoadingKpi = false;
  isLoadingGoals = false;
  isLoadingMeetings = false;
  isLoadingTasks = false;
  selectedHospitalId = 0;
  hospital: any = null;

  selectedQuarter = 'default';
  quarterList: any[] = [];
  currentQtr: any = null;
  showPastQuarters = false;

  taskList: any[] = [];
  pastDueTasks: any[] = [];
  upcomingTasks: any[] = [];

  nextMeeting: HospitalMeeting | null = null;
  remainingMeetings: HospitalMeeting[] = [];
  hospitalList: any[] = [];

  expandedGoalTargetId: number | null = null;

  filterGoalStatuses: string[] = [];
  filterExecutionHealths: string[] = [];

  goalStatusOptions = [
    { label: 'On Track', value: 'ON_TRACK' },
    { label: 'At Risk', value: 'At_Risk' },
    { label: 'Off Track', value: 'Off_Track' },
  ];
  executionHealthOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Slowing', value: 'SLOWING' },
    { label: 'Stalled', value: 'STALLED' },
  ];
  filterRmAttentionOnly = false;

  activityFeed: { at: string; category: string; action: string; comment: string }[] = [];


  /** Latest OPS + goals payload from `getOpsHospitalForHospital` (quarter-specific). */
  private lastOpsHospitalRow: any | null = null;

  /** Stable record from GET v1/goalhospital/hospital/{id} (dbo.usp_GetHospitalById). */
  private hospitalProfile: any | null = null;
  isAdminLtk = false;
  private routeParamSub: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private leaderService: LeaderService,
    private location: Location,
    private dialog: MatDialog,
    private authMgr: AuthManager
  ) {}

  currentUserId: number | null = null;
  currentUserName = '';

  ngOnInit(): void {
    this.authMgr.getCurrentUser().subscribe((user: any) => {
      this.currentUserId = user?.id ?? null;
      this.currentUserName = user?.displayName || user?.name || '';
    });
    this.getHospitalList();
    this.checkAdminAccess();
    this.routeParamSub = this.route.paramMap.subscribe(params => {
      const newId = Number(params.get('hospitalid') || 0);
      if (newId === this.selectedHospitalId && this.hospital !== null) return;
      this.selectedHospitalId = newId;
      this.selectedQuarter = this.route.snapshot.queryParamMap.get('quarter') || 'default';
      this.resetHospitalState();
      this.loadHospitalProfile();
      this.loadQuarters();
    });
  }

  private resetHospitalState(): void {
    this.isLoadingKpi = true;
    this.isLoadingGoals = true;
    this.isLoadingMeetings = true;
    this.isLoadingTasks = true;
    this.hospital = null;
    this.lastOpsHospitalRow = null;
    this.hospitalProfile = null;
    this.taskList = [];
    this.pastDueTasks = [];
    this.upcomingTasks = [];
    this.nextMeeting = null;
    this.remainingMeetings = [];
    this.activityFeed = [];
    this.quarterList = [];
    this.currentQtr = null;
    this.expandedGoalTargetId = null;
  }

  ngOnDestroy(): void {
    this.routeParamSub?.unsubscribe();
  }

  private checkAdminAccess(): void {
    this.leaderService.hasAccessToManageUsersLtk().subscribe((res: any) => {
      this.isAdminLtk = !!res?.data;
    });
  }

  locationBack(): void {
    this.location.back();
  }

  getHospitalList(): void {
    this.leaderService.getHospitalList().subscribe((res: any) => {
      if (Array.isArray(res?.data)) {
        this.hospitalList = res.data;
      }
    });
  }

  /** Loads dbo.usp_GetHospitalById via GET v1/goalhospital/hospital/{hospitalId}. */
  private loadHospitalProfile(): void {
    if (!this.selectedHospitalId) {
      return;
    }
    this.leaderService.getHospitalById(this.selectedHospitalId).subscribe({
      next: (res: any) => {
        const d = res?.data;
        this.hospitalProfile = d ? this.mapHospitalByIdResponse(d) : null;
        this.hospital = this.composeHospitalWorkspace(this.lastOpsHospitalRow);
      },
      error: () => {
        this.hospitalProfile = null;
        this.hospital = this.composeHospitalWorkspace(this.lastOpsHospitalRow);
      }
    });
  }

  private mapHospitalByIdResponse(d: any): any {
    return {
      ...d,
      hospitalId: d.practiceSKey ?? this.selectedHospitalId,
      hospitalName: d.displayName ?? d.practiceName ?? ''
    };
  }

  loadQuarters(): void {
    this.leaderService.getQtrList().subscribe((res: any) => {
      if (Array.isArray(res?.data)) {
        this.quarterList = res.data;
      }
      if (!this.selectedQuarter || this.selectedQuarter === 'default') {
        const currentQuarter = this.quarterList.find((q: any) => q?.currentQuarter);
        if (currentQuarter?.quarterName) {
          this.selectedQuarter = currentQuarter.quarterName;
        }
      }
      this.applyQuarter();
    });
  }

  getResolvedQuarter(): string {
    const q = (this.selectedQuarter || '').toString().trim();
    if (!q || q.toLowerCase() === 'default') {
      const currentQuarter = this.quarterList.find((x: any) => x?.currentQuarter);
      if (currentQuarter?.quarterName) {
        return currentQuarter.quarterName;
      }
      return 'default';
    }
    return q;
  }

  onQuarterChange(): void {
    this.applyQuarter();
  }

  selectQuarterTab(name: string): void {
    this.selectedQuarter = name;
    this.applyQuarter();
  }

  applyQuarter(): void {
    const resolved = this.getResolvedQuarter();
    this.selectedQuarter = resolved;
    this.currentQtr = this.quarterList.find((q: any) => q.quarterName === resolved) || null;
    this.syncQuarterQueryParam(resolved);
    this.loadHospitalWorkspace(resolved);
  }

  /** Keeps `?quarter=` in the URL in sync with the selected quarter (bookmarkable / shareable). */
  private syncQuarterQueryParam(resolved: string): void {
    const inUrl = this.route.snapshot.queryParamMap.get('quarter') ?? '';
    const next = resolved && resolved !== 'default' ? resolved : '';
    if (inUrl === next) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { quarter: next || null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  loadHospitalWorkspace(quarter: string): void {
    if (!this.selectedHospitalId) {
      return;
    }
    this.isLoadingKpi = true;
    this.isLoadingGoals = true;
    this.leaderService.getOpsHospitalData(quarter, this.selectedHospitalId).subscribe({
      next: (res: any) => {
        const data = res?.data;
        this.lastOpsHospitalRow = Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
        this.hospital = this.composeHospitalWorkspace(this.lastOpsHospitalRow);
        this.activityFeed = [];
        this.isLoadingKpi = false;
        this.isLoadingGoals = false;
        if (this.hospital) {
          this.loadTaskList(quarter);
          this.loadHospitalMeetings(quarter);
        } else {
          this.isLoadingTasks = false;
          this.isLoadingMeetings = false;
          this.taskList = [];
          this.pastDueTasks = [];
          this.upcomingTasks = [];
          this.nextMeeting = null;
          this.remainingMeetings = [];
        }
      },
      error: () => {
        this.lastOpsHospitalRow = null;
        this.hospital = this.composeHospitalWorkspace(null);
        this.isLoadingKpi = false;
        this.isLoadingGoals = false;
        if (this.hospital) {
          this.loadTaskList(quarter);
          this.loadHospitalMeetings(quarter);
        } else {
          this.isLoadingTasks = false;
          this.isLoadingMeetings = false;
          this.taskList = [];
          this.pastDueTasks = [];
          this.upcomingTasks = [];
          this.nextMeeting = null;
          this.remainingMeetings = [];
        }
      }
    });
  }

  /**
   * Identity from GET v1/goalhospital/hospital/{id}, merged with quarter OPS from GET .../ops-hospital/{id}/{quarter}.
   */
  private composeHospitalWorkspace(opsRow: any | null): any | null {
    const profile = this.hospitalProfile;
    if (!profile && !opsRow) {
      return null;
    }
    const merged: any = { ...(profile || {}), ...(opsRow || {}) };
    merged.hospitalId = this.selectedHospitalId;
    merged.hospitalName =
      opsRow?.hospitalName ??
      profile?.hospitalName ??
      profile?.practiceName ??
      profile?.displayName ??
      '';
    merged.goals = Array.isArray(opsRow?.goals) ? opsRow.goals : [];
    merged.rmAttentionRequired = opsRow?.rmActionRequired ?? merged.rmActionRequired;
    merged.leadershipAttentionRequired = this.normalizeLeadershipAttentionRequired(
      merged.leadershipAttentionRequired
    );
    return merged;
  }

  loadTaskList(quarter: string): void {
    this.isLoadingTasks = true;
    this.isLoading = true;
    this.leaderService.getTaskList(quarter, this.selectedHospitalId).subscribe({
      next: (response: any) => {
        this.taskList = Array.isArray(response?.data?.tasksDetails)
          ? response.data.tasksDetails
          : [];
        this.separateTasks();
        this.isLoadingTasks = false;
        this.isLoading = false;
      },
      error: () => {
        this.taskList = [];
        this.pastDueTasks = [];
        this.upcomingTasks = [];
        this.isLoadingTasks = false;
        this.isLoading = false;
      }
    });
  }

  separateTasks(): void {
    this.pastDueTasks = this.taskList.filter(
      (task) => task.statusName && task.statusName.toLowerCase() === 'past due'
    );
    this.upcomingTasks = this.taskList.filter(
      (task) => task.statusName && task.statusName.toLowerCase() === 'open'
    );
  }

  private getMeetingDateTime(dateValue: string, timeValue: string): Date {
    const [year, month, day] = (dateValue || '').split('-').map((value) => Number(value));
    const [hours = 0, minutes = 0, seconds = 0] = (timeValue || '00:00:00')
      .split(':')
      .map((value) => Number(value));
    return new Date(year || 0, (month || 1) - 1, day || 1, hours, minutes, seconds, 0);
  }

  private getMeetingStartDateTime(meeting: HospitalMeeting): Date {
    return this.getMeetingDateTime(meeting?.scheduledDate, meeting?.startTime);
  }

  private getMeetingEndDateTime(meeting: HospitalMeeting): Date {
    return this.getMeetingDateTime(meeting?.scheduledDate, meeting?.endTime);
  }

  loadHospitalMeetings(_quarter?: string): void {
    this.isLoadingMeetings = true;
    this.leaderService.getScheduledMeetings('upcoming', this.selectedHospitalId).subscribe({
      next: (res: any) => {
        let meetings: HospitalMeeting[] = [];
        if (Array.isArray(res?.data)) {
          meetings = res.data;
        } else if (Array.isArray(res?.data?.meetingDetails)) {
          meetings = res.data.meetingDetails;
        }

        const sorted = [...meetings].sort(
          (a, b) =>
            this.getMeetingStartDateTime(a).getTime() - this.getMeetingStartDateTime(b).getTime()
        );

        const now = new Date();
        const meetingsToDisplay = sorted.filter(
          (m) => this.getMeetingEndDateTime(m).getTime() >= now.getTime()
        );

        this.nextMeeting = meetingsToDisplay.length ? meetingsToDisplay[0] : null;
        this.remainingMeetings = meetingsToDisplay.slice(1, 4);
        this.isLoadingMeetings = false;
      },
      error: () => {
        this.nextMeeting = null;
        this.remainingMeetings = [];
        this.isLoadingMeetings = false;
      }
    });
  }

  get pacePercent(): number | null {
    const p = this.hospital?.pace;
    if (p == null || Number.isNaN(Number(p))) {
      return null;
    }
    return Math.round(Number(p) * 100);
  }

  get attentionSummary(): string {
    const h = this.hospital?.leadershipAttentionRequired || '';
    const key = this.normalizeLeadershipAttentionRequiredKey(h);
    if (key === 'ATTENTION_REQUIRED') {
      return 'Yes';
    }
    if (key === 'MONITOR') {
      return 'Monitor';
    }
    if (key === 'NO_ATTENTION_NEEDED') {
      return 'No';
    }
    return '';
  }

  get goalsMaster(): any[] {
    const goals = Array.isArray(this.hospital?.goals) ? this.hospital.goals : [];
    return goals.filter((g: any) => this.passesGoalFilters(g));
  }

  get liveGoals(): any[] {
    return this.goalsMaster.filter((g: any) => !this.isGoalPendingReview(g));
  }

  /** VP/DO Review + RM Review workflow — not pace/OPS buckets and not RM "Action Required" pill. */
  get pendingReviewGoals(): any[] {
    return this.goalsMaster.filter((g: any) => this.isGoalPendingReview(g));
  }

  private passesGoalFilters(goal: any): boolean {
    if (this.filterGoalStatuses && this.filterGoalStatuses.length) {
      const s = (goal?.status || '').toString().trim();
      if (!this.filterGoalStatuses.includes(s)) {
        return false;
      }
    }
    if (this.filterExecutionHealths && this.filterExecutionHealths.length) {
      const ex = (goal?.executionHealth || '').toString().toUpperCase();
      const wants = this.filterExecutionHealths.map((f: string) => f.toUpperCase());
      if (!wants.some((want: string) => ex.includes(want))) {
        return false;
      }
    }
    return true;
  }

  isGoalPendingReview(goal: any): boolean {
    // Check by currentStatusId (13 = PENDING_RM_REVIEW, 14 = PENDING_VP_REVIEW, 15 = PENDING_DO_REVIEW)
    const statusId = goal?.currentStatusId ?? goal?.CurrentStatusId;
    if (statusId === 13 || statusId === 14 || statusId === 15) {
      return true;
    }
    // Fallback: check workflowStatus string
    const w = (goal?.workflowStatus ?? goal?.WorkflowStatus ?? '')
      .toString()
      .trim()
      .toUpperCase();
    return (
      w === 'PENDING_VP_REVIEW' ||
      w === 'PENDING_DO_REVIEW' ||
      w === 'PENDING_RM_REVIEW'
    );
  }

  /** Display label for workflow row (aligns with goals-page). */
  getGoalWorkflowLabel(goal: any): string {
    const w = (goal?.workflowStatus ?? goal?.WorkflowStatus ?? '')
      .toString()
      .trim()
      .toUpperCase();
    if (w === 'PENDING_VP_REVIEW' || w === 'PENDING_DO_REVIEW') {
      return 'VP/DO Review';
    }
    if (w === 'PENDING_RM_REVIEW') {
      return 'RM Review';
    }
    return '';
  }

  toggleGoal(goalTargetId: number): void {
    this.expandedGoalTargetId =
      this.expandedGoalTargetId === goalTargetId ? null : goalTargetId;
  }

  isGoalExpanded(goalTargetId: number): boolean {
    return this.expandedGoalTargetId === goalTargetId;
  }

  /** Pace from API as 0–1 fraction; empty when missing or invalid. */
  formatGoalPacePercent(goal: any): string {
    const p = goal?.pace;
    if (p == null || !Number.isFinite(Number(p))) {
      return '';
    }
    return `${Math.round(Number(p) * 100)}%`;
  }

  clearGoalFilters(): void {
    this.filterGoalStatuses = [];
    this.filterExecutionHealths = [];
  }

  /**
   * Parses quarter name like "2Q26" → { qNum: 2, year: 2026 }.
   * Returns null if the name doesn't match the expected pattern.
   */
  private parseQuarterName(name: string): { qNum: number; year: number } | null {
    const match = (name ?? '').match(/^(\d)Q(\d{2})$/i);
    if (!match) return null;
    return { qNum: parseInt(match[1], 10), year: 2000 + parseInt(match[2], 10) };
  }

  private isQuarterPast(quarterName: string): boolean {
    const parsed = this.parseQuarterName(quarterName);
    if (!parsed) return false;
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentQNum = Math.ceil((today.getMonth() + 1) / 3);
    return parsed.year < currentYear ||
      (parsed.year === currentYear && parsed.qNum < currentQNum);
  }

  /** Quarters before the current one, newest first. */
  get pastQuarters(): any[] {
    return this.quarterList
      .filter((q: any) => this.isQuarterPast(q.quarterName))
      .sort((a: any, b: any) => {
        const pa = this.parseQuarterName(a.quarterName)!;
        const pb = this.parseQuarterName(b.quarterName)!;
        return pb.year !== pa.year ? pb.year - pa.year : pb.qNum - pa.qNum;
      });
  }

  /** Current quarter and future quarters — shown by default. */
  get activeQuarters(): any[] {
    return this.quarterList.filter((q: any) => !this.isQuarterPast(q.quarterName));
  }

  togglePastQuarters(): void {
    this.showPastQuarters = !this.showPastQuarters;
  }

  scrollToGoals(): void {
    const el = document.getElementById('hospital-goals');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  joinMeeting(url: string): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  openMeetingDetail(row: HospitalMeeting): void {
    this.dialog.open(MeetingDetailDialogComponent, {
      width: '500px',
      data: row
    });
  }

  openSchedulingPage(): void {
    this.router.navigate(['/leader-toolkit-scheduling']);
  }

  newAddMeeting(): void {
    const dialogRef = this.dialog.open(NewMeetingSchedulerComponent, {
      width: '850px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: this.hospitalList,
      disableClose: false
    });
    dialogRef.afterClosed().subscribe((result) => {
      const ok =
        result === 'success' ||
        result?.status === 'success' ||
        result?.refreshMeetings === true;
      if (ok) {
        this.loadHospitalMeetings();
      }
    });
  }

openViewTask(taskData: any, page: 'tasks' | 'goals' = 'tasks'): void {
     const isMulti = Array.isArray(taskData);
     const dialogRef = this.dialog.open(ViewTaskModalComponent, {
       data: {
         taskId: taskData,
         page: isMulti ? 'goals' : page,
         quarter: this.getResolvedQuarter()
       },
      width: '600px',
      disableClose: true
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.applyQuarter();
      }
    });
  }

  taskListPage(): void {
    this.router.navigate(['/leader-toolkit-tasks']);
  }

  createNewTask(): void {
    const dialogRef = this.dialog.open(CreateNewTaskComponent, {});
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.applyQuarter();
      }
    });
  }

  openCreateTask(goal?: any): void {
    const quarterName = this.getResolvedQuarter();
    const quarter = this.quarterList.find((q: any) => q?.quarterName === quarterName) || null;
    const dialogRef = this.dialog.open(CreateNewTaskComponent, {
      data: {
        hospital: this.hospital,
        quarter,
        goal: goal
          ? {
              goalTargetId: goal.goalTargetId ?? null,
              goalName: goal.goalName ?? null
            }
          : null
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.applyQuarter();
      }
    });
  }

  /**
   * Opens the shared "Assign New Goal(s)" dialog, pre-scoped to the hospital and
   * quarter this dashboard is currently showing. The hospital/quarter picker in
   * AssignGoalsModalComponent is intentionally skipped — both are already
   * resolved from the route here.
   */
  openAssignGoal(): void {
    const quarter = this.currentQtr
      || this.quarterList.find((q: any) => q?.quarterName === this.getResolvedQuarter())
      || null;

    if (!this.selectedHospitalId || !quarter) {
      this.leaderService.showNotification('Select a hospital and quarter before assigning goals.', true);
      return;
    }

    const hospital = {
      ...(this.hospital || {}),
      practiceSKey: this.hospital?.practiceSKey ?? this.selectedHospitalId
    };

    const dialogRef = this.dialog.open(AssginNewGoalPageComponent, {
      data: { hospital, quarter },
      autoFocus: false
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.applyQuarter();
      }
    });
  }

  viewGoalPage(goal: any): void {
    this.openGoalDetails(goal, 'view');
  }

  editGoal(goal: any): void {
    this.openGoalDetails(goal, 'edit');
  }

  private openGoalDetails(goal: any, mode: 'view' | 'edit'): void {
    const dialogRef = this.dialog.open(GoalDetailsComponent, {
      data: goal.goalTargetId,
      width: '900px',
      maxWidth: '95vw'
    });

    if (mode === 'edit') {
      dialogRef.componentInstance.isEditMode = true;
    }

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'success') {
        this.applyQuarter();
      }
    });
  }

  deleteGoal(goal: any): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Goal',
        message: `Are you sure you want to delete the goal "${goal.goalName}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        isDestructive: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const goalId = goal.goalId ?? goal.goalTargetId;
        this.leaderService.deleteGoalDictionary(goalId).subscribe({
          next: (res: any) => {
            if (res?.resultDetails?.[0]?.status === 'SUCCESS') {
              this.leaderService.showNotification('Goal deleted successfully', false);
              this.applyQuarter();
            } else {
              this.leaderService.showNotification(res?.resultDetails?.[0]?.message || 'Failed to delete goal', true);
            }
          },
          error: () => {
            this.leaderService.showNotification('An error occurred while deleting the goal', true);
          }
        });
      }
    });
  }

  getHospitalTaskInfos(goals: any[]): any[] {
    if (!Array.isArray(goals) || !goals.length) {
      return [];
    }
    const all = goals.flatMap((goal: any) =>
      Array.isArray(goal?.taskInfos) ? goal.taskInfos : []
    );
    const map = new Map<number, any>();
    all.forEach((task: any) => {
      const id = Number(task?.taskId);
      if (Number.isFinite(id) && id > 0 && !map.has(id)) {
        map.set(id, task);
      }
    });
    return Array.from(map.values());
  }

  getTotalAssignedTaskCount(goals: any[]): number {
    if (!goals?.length) {
      return 0;
    }
    return goals.reduce((sum, g) => {
      const n = g.taskCount;
      return sum + (typeof n === 'number' && Number.isFinite(n) ? n : 0);
    }, 0);
  }

  getTotalCompletedTaskCount(goals: any[]): number {
    if (!goals?.length) {
      return 0;
    }
    return goals.reduce((sum, g) => {
      const n = g.taskComplete;
      return sum + (typeof n === 'number' && Number.isFinite(n) ? n : 0);
    }, 0);
  }

  getMomentumClass(momentum: string): string {
    if (!momentum) {
      return '';
    }
    const m = momentum.toString().toUpperCase();
    if (m === 'IMPROVING') {
      return 'improving';
    }
    if (m === 'DECLINING') {
      return 'declining';
    }
    if (m === 'FLAT') {
      return 'flat';
    }
    return '';
  }

  getMomentumIcon(momentum: string): string {
    if (!momentum) {
      return 'remove';
    }
    const m = momentum.toString().toUpperCase();
    if (m === 'DECLINING') {
      return 'arrow_downward';
    }
    if (m === 'FLAT') {
      return 'remove';
    }
    return 'arrow_upward';
  }

  getMomentumLabel(momentum: string): string {
    if (!momentum) {
      return '';
    }
    const m = momentum.toString().toUpperCase();
    if (m === 'DECLINING') {
      return 'Declining';
    }
    if (m === 'FLAT') {
      return 'Flat';
    }
    return 'Improving';
  }

  isOnTrackStatus(status: string): boolean {
    const normalized = (status || '')
      .toString()
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');
    return normalized === 'ON_TRACK';
  }

  /** Returns the canonical status key (ON_TRACK | At_Risk | Off_Track) for safe use in [ngClass]. */
  normalizeStatusClass(status: string): string {
    const s = (status || '').toString().trim().toUpperCase().replace(/[\s_-]+/g, '_');
    if (s === 'ON_TRACK') return 'ON_TRACK';
    if (s === 'AT_RISK') return 'At_Risk';
    if (s === 'OFF_TRACK') return 'Off_Track';
    return '';
  }

  getStatusLabel(status: string): string {
    const normalized = this.normalizeStatusClass(status);
    if (normalized === 'ON_TRACK') return 'On Track';
    if (normalized === 'At_Risk') return 'At Risk';
    if (normalized === 'Off_Track') return 'Off Track';
    return '';
  }

  getExecutionHealthClass(executionHealth: string): string {
    if (!executionHealth) {
      return '';
    }
    const value = executionHealth.toString().toUpperCase().replace(/[_-]+/g, ' ');
    if (value.includes('ACTIVE') || value.includes('GREEN')) {
      return 'active';
    }
    if (value.includes('SLOWING') || value.includes('YELLOW')) {
      return 'slowing';
    }
    if (value.includes('STALLED') || value.includes('RED')) {
      return 'stalled';
    }
    return '';
  }

  getExecutionHealthLabel(executionHealth: string): string {
    if (!executionHealth) {
      return '';
    }
    const value = executionHealth.toString().toUpperCase().replace(/[_-]+/g, ' ');
    if (value.includes('ACTIVE') || value.includes('GREEN')) {
      return 'ACTIVE';
    }
    if (value.includes('SLOWING') || value.includes('YELLOW')) {
      return 'SLOWING';
    }
    if (value.includes('STALLED') || value.includes('RED')) {
      return 'STALLED';
    }
    return value;
  }

  getRmAttentionRequiredLabel(value: string): string {
    const normalized = (value || '').toString().trim().toUpperCase();
    if (normalized === 'ACTION REQUIRED') {
      return 'Action Required';
    }
    if (normalized === 'MONITOR') {
      return 'Monitor';
    }
    if (normalized === 'NO ACTION NEEDED') {
      return 'No Action Needed';
    }
    return '';
  }

  getRmAttentionRequiredClass(value: string): string {
    const normalized = this.getRmAttentionRequiredLabel(value).toUpperCase();
    if (normalized === 'ACTION REQUIRED') {
      return 'action-required';
    }
    if (normalized === 'MONITOR') {
      return 'monitor';
    }
    if (normalized === 'NO ACTION NEEDED') {
      return 'no-action-needed';
    }
    return '';
  }

  getLeadershipAttentionLabel(value: any): string {
    return this.normalizeLeadershipAttentionRequired(value);
  }

  private normalizeLeadershipAttentionRequired(value: any): string {
    const normalized = (value || '').toString().trim().toUpperCase().replace(/\s+/g, ' ');
    if (normalized === 'ATTENTION REQUIRED') {
      return 'Attention Required';
    }
    if (normalized === 'MONITOR') {
      return 'Monitor';
    }
    if (
      normalized === 'NO ATTENTION NEEDED' ||
      normalized === 'NO ATTENSION REQUIRED' ||
      normalized === 'NO ATTENTION REQUIRED'
    ) {
      return 'No Attention Needed';
    }
    return '';
  }

  private normalizeLeadershipAttentionRequiredKey(value: any): string {
    const label = this.normalizeLeadershipAttentionRequired(value);
    if (label === 'Attention Required') {
      return 'ATTENTION_REQUIRED';
    }
    if (label === 'Monitor') {
      return 'MONITOR';
    }
    if (label === 'No Attention Needed') {
      return 'NO_ATTENTION_NEEDED';
    }
    return '';
  }
}
